import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '' // Bruker en relativ sti i produksjon
  : 'http://localhost:5000'; // Beholder den gamle for lokal utvikling

// Ikoner for lydknappen
const IconVolumeOn = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path></svg>;
const IconVolumeOff = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"></path></svg>;

function HvaErNaprapati() {
  const [content, setContent] = useState({});
  const [conditions, setConditions] = useState([]);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/content/naprapati`).then(res => {
      const organized = res.data.reduce((acc, item) => ({ ...acc, [item.element_id]: item }), {});
      setContent(organized);
    });
    axios.get(`${API_URL}/api/conditions`).then(res => setConditions(res.data));
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation(); // Hindrer at videoen pauser når man trykker på lydknappen
    if (videoRef.current) {
      const currentlyMuted = videoRef.current.muted;
      videoRef.current.muted = !currentlyMuted;
      setIsMuted(!currentlyMuted);
    }
  };

  return (
    <div>
      <Helmet>
      <title>Hva er Naprapati? – Sunnfjord Naprapatsenter</title>
      <meta name="description" content="Lær mer om hva en naprapat gjør. Vi behandler hodepine, ryggsmerter, isjias og andre muskel- og leddplager i Førde." />
    </Helmet>
      {/* --- SEKSJON 1: TEKST OVER VIDEO --- */}
      <section className="intro-text-section">
        <h1>{content.naprapati_hero_tittel?.value}</h1>
        <p>{content.naprapati_hero_p1?.value}</p>
        <p>{content.naprapati_hero_p2?.value}</p>
      </section>

      {/* --- SEKSJON 2: VIDEO MED LYDKNAPP --- */}
      <section className="video-section" onClick={handlePlayPause}>
        <video 
          ref={videoRef} 
          className="video-background" 
          loop 
          playsInline 
          src="/videos/background-video.mp4"
          poster="/images/video-poster.jpg" 
        ></video>
        
        {!isPlaying && (
          <div className="video-play-button-overlay">
            <button className="video-play-button"></button>
          </div>
        )}
        
        <button onClick={toggleMute} className="video-mute-button">
          {isMuted ? <IconVolumeOff /> : <IconVolumeOn />}
        </button>
      </section>

      {/* --- SEKSJON 3: BEHANDLINGER --- */}
      <section className="page-section">
        <h2>{content.naprapati_behandlinger_tittel?.value}</h2>
        <p>{content.naprapati_behandlinger_p1?.value}</p>
        <p><strong>{content.naprapati_behandlinger_p2?.value}</strong></p>
      </section>

      {/* --- SEKSJON 4: TRYGG BEHANDLING (UTHEVET) --- */}
      <section className="page-section">
        {/* Bygger opp tittelen fra de tre delene */}
        <h2>
          {content.naprapati_trygg_tittel_del1?.value}{' '}
          <span className="underline-text">{content.naprapati_trygg_tittel_uthevet?.value}</span>{' '}
          {content.naprapati_trygg_tittel_del2?.value}
        </h2>
        <p>{content.naprapati_trygg_tekst?.value}</p>
      </section>
      
      {/* --- SEKSJON 5: VIDEOKONSULTASJON --- */}
      <section className='page-section'>
        <div className="two-column-section">
          <div className="text-column">
            <h2>{content.naprapati_video_overskrift1?.value}</h2>
            <p>{content.naprapati_video_p1?.value}</p>
            <h3>{content.naprapati_video_overskrift2?.value}</h3>
            <p>{content.naprapati_video_p2?.value}</p>
            <p>{content.naprapati_video_p3?.value}</p>
            <a href="https://napforde.bestille.no/" target="_blank" rel="noopener noreferrer" className="button">
              Bestill videokonsultasjon
            </a>
          </div>
          <div className="image-column">
            {content.naprapati_video_bilde && 
              <img src={content.naprapati_video_bilde.value} alt="Videokonsultasjon med naprapat" />
            }
          </div>
        </div>
      </section>

      {/* --- SEKSJON 6: GRID MED TILSTANDER --- */}
      <section className="page-section">
        <h2>{content.naprapati_plager_tittel?.value}</h2>
        <div className="conditions-grid">
          {conditions.map(condition => (
            <div key={condition.slug} className="condition-card">
              <img src={condition.image_url} alt={condition.name} />
              <div className="card-content">
                <h3>{condition.name}</h3>
                <p>{condition.short_description}</p>
                <Link to={`/tilstander/${condition.slug}`} className="button">
                  Les mer
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default HvaErNaprapati;